import { ActivatedRoute, ActivatedRouteSnapshot, Data, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { RegisterData } from '../resolvers/register.resolver';

export interface TypedActivatedRoute<
  TParams extends Params = {},
  TData extends Data = {},
  TQueryParams extends Params = {},
> extends ActivatedRoute {
  params: Observable<TParams>;
  queryParams: Observable<TQueryParams>;
  data: Observable<TData>;

  snapshot: TypedActivatedRouteSnapshot<TParams, TData, TQueryParams>;

  firstChild: TypedActivatedRoute<TParams, TData, TQueryParams> | null;
  children: Array<TypedActivatedRoute<TParams, TData, TQueryParams>>;
}

export interface TypedActivatedRouteSnapshot<
  TParams extends Params = {},
  TData extends Data = {},
  TQueryParams extends Params = {},
> extends ActivatedRouteSnapshot {
  params: TParams;
  queryParams: TQueryParams;
  data: TData;

  firstChild: TypedActivatedRouteSnapshot<TParams, TData, TQueryParams> | null;
  children: Array<TypedActivatedRouteSnapshot<TParams, TData, TQueryParams>>;
}

export const ROUTE_PERIOD_SEPARATOR = '~';

export function getChildRoute<T extends ActivatedRoute | ActivatedRouteSnapshot>(route: T): T {
  let current = route;
  while (current.firstChild) {
    current = current.firstChild as T;
  }
  return current;
}

function toPathFromRoot(snapshot: ActivatedRouteSnapshot) {
  return getChildRoute(snapshot).pathFromRoot;
}

function createUrlParts(path: string | number, parameters: Params) {
  const result: Array<string | Params> = [];
  if (path) {
    result.push(path.toString());
  }
  if (Object.keys(parameters).length > 0) {
    result.push(parameters);
  }

  return result;
}

export async function navigateToRouteWithReplacedParameters(
  router: Router,
  paramMap: {
    [name: string]: string | number;
  },
  route?: ActivatedRouteSnapshot,
  options?: {
    replaceUrl?: boolean;
  },
) {
  const currentRoute = route || getChildRoute(router.routerState.snapshot.root);

  if (
    Object.keys(paramMap).every(
      paramName => currentRoute.params[paramName] === paramMap[paramName].toString(),
    )
  ) {
    return false;
  }

  const newRoute = replaceParametersInRoute(currentRoute, paramMap);

  return router.navigate(newRoute, {
    queryParams: currentRoute.queryParams,
    fragment: currentRoute.fragment || undefined,
    ...options,
  });
}

export function getUrlWithReplacedParameters(
  router: Router,
  currentRoute: ActivatedRouteSnapshot,
  paramMap: {
    [name: string]: string | number;
  },
) {
  const newRoute = replaceParametersInRoute(currentRoute, paramMap);

  return router.serializeUrl(router.createUrlTree(newRoute));
}

export function getCurrentRegisterLocale(
  routeSnapshot?: TypedActivatedRouteSnapshot<{}, Partial<RegisterData>>,
) {
  const { register, organisation } = routeSnapshot
    ? getChildRoute(routeSnapshot).data
    : {
        register: null,
        organisation: null,
      };

  return register?.locale || organisation?.activeOrganisation?.locale || 'en-US';
}

function replaceParametersInRoute(
  snapshot: ActivatedRouteSnapshot,
  paramMap: {
    [name: string]: string | number;
  },
): Array<string | Params> {
  const pathFromRoot = toPathFromRoot(snapshot).filter(ars => ars.url.length > 0);

  const paramAdded: { [name: string]: true | undefined } = {};

  if (pathFromRoot.length === 0) {
    return createUrlParts('', paramMap);
  }

  const result: Array<string | Params> = ['/'];

  pathFromRoot.forEach(ars => {
    const pathOverrides: {
      [key: number]: string | undefined;
    } = {};
    // Does it have a required param, e.g. `registers/:registerId`
    if (ars.routeConfig && ars.routeConfig.path) {
      const pathParts = ars.routeConfig.path.split(/\//g);
      pathParts.forEach((path, i) => {
        if (path[0] === ':') {
          const paramName = path.substring(1);
          if (paramMap[paramName]) {
            pathOverrides[i] = paramMap[paramName].toString();
            paramAdded[paramName] = true;
          }
        }
      });
    }

    ars.url.forEach((segment, i) => {
      const pathOverride = pathOverrides[i];
      if (pathOverride) {
        result.push(pathOverride);
      } else {
        result.push(...createUrlParts(segment.path, segment.parameters));
      }
    });

    const params = createParams(Object.keys(ars.params));
    if (params) {
      result.push(params);
    }
  });

  const finalParams = createParams(Object.keys(paramMap));
  if (finalParams) {
    result.push(finalParams);
  }

  return result;

  function createParams(keys: string[]): Params | null {
    let params: Params | null = null;
    keys.forEach(paramName => {
      if (paramMap[paramName] && !paramAdded[paramName]) {
        params = {
          ...(params || {}),
          [paramName]: paramMap[paramName],
        };
      }
    });

    return params;
  }
}
